import type { PuzzleShape } from '@arrow/puzzle-schema';
import { memo, useMemo } from 'react';

/**
 * A puzzle's cover: its shape and nothing else (clue blocks, letter cells, the arrows'
 * tiny wedges, the frame), drawn from the listing's `shape`, so no puzzle is fetched and
 * nothing in it can spoil. Five SVG elements whatever the size, so a list of hundreds
 * stays light. Colours are the cover tokens (play.css), lifted at night so a small board
 * still reads on the dark stock.
 */
const S = 10;

function draw(shape: PuzzleShape) {
	const h = shape.grid.length;
	const w = shape.grid[0]?.length ?? 0;
	let blocks = '';
	let blanks = '';
	shape.grid.forEach((row, y) => {
		for (let x = 0; x < row.length; x++) {
			const ch = row[x];
			if (ch === '#') blocks += `M${x * S} ${y * S}h${S}v${S}h-${S}z`;
			else if (ch === '.') blanks += `M${x * S} ${y * S}h${S}v${S}h-${S}z`;
		}
	});
	let rules = '';
	for (let i = 1; i < w; i++) rules += `M${i * S} 0V${h * S}`;
	for (let i = 1; i < h; i++) rules += `M0 ${i * S}H${w * S}`;
	// Two clues in a cell share it, top half and bottom half.
	const perCell = new Map<string, number>();
	for (const [r, c] of shape.arrows) perCell.set(`${r},${c}`, (perCell.get(`${r},${c}`) ?? 0) + 1);
	const seen = new Map<string, number>();
	let arrows = '';
	for (const [r, c, dir] of shape.arrows) {
		const k = `${r},${c}`;
		const slot = seen.get(k) ?? 0;
		seen.set(k, slot + 1);
		const two = (perCell.get(k) ?? 1) > 1;
		const x = c * S;
		const y = r * S;
		const mid = two ? (slot === 0 ? S * 0.28 : S * 0.72) : S / 2;
		if (dir === 'right' || dir === 'right-down') arrows += `M${x + S - 3.4} ${y + mid - 2.2}l2.6 2.2l-2.6 2.2z`;
		else arrows += `M${x + S / 2 - 2.2} ${y + S - 3.4}l2.2 2.6l2.2-2.6z`;
	}
	return { w, h, blocks, blanks, rules, arrows };
}

export const Cover = memo(function Cover({ shape, className, label }: { shape: PuzzleShape; className?: string; label?: string }) {
	const d = useMemo(() => draw(shape), [shape]);
	const W = d.w * S;
	const H = d.h * S;
	if (!W || !H) return <span className={`cover ${className ?? ''}`} aria-hidden="true" />;
	return (
		<svg
			className={`cover ${className ?? ''}`}
			viewBox={`0 0 ${W} ${H}`}
			role={label ? 'img' : undefined}
			aria-label={label}
			aria-hidden={label ? undefined : true}
			data-size={Math.max(d.w, d.h) > 10 ? 'big' : undefined}
		>
			<rect className="cv-cell" width={W} height={H} />
			{d.blanks && <path className="cv-blank" d={d.blanks} />}
			<path className="cv-rules" d={d.rules} />
			{d.blocks && <path className="cv-block" d={d.blocks} />}
			{d.arrows && <path className="cv-arrows" d={d.arrows} />}
			<rect className="cv-frame" width={W} height={H} />
		</svg>
	);
});
